<template>
    <div
        class="ds-dropdown"
        :class="{
            'align-start': !arrow && align === 'start',
            'align-end': !arrow && align === 'end',
            open,
        }"
    >
        <div ref="buttonContainerRef" class="button-container" @click="open = (discardMenu ? false : !open)">
            <slot name="button" :open="open">
                <button class="ds-btn" :class="buttonClass" :aria-expanded="open">
                    {{ text }}
                </button>
            </slot>
        </div>
        <Transition :name="listTransition">
            <div
                v-if="open"
                ref="menuRef"
                v-click-outside="{
                    exclude: ['buttonContainerRef'],
                    handler: () => open = false,
                }"
                class="menu"
                :class="{
                    arrow,
                    flip,
                    'no-gap': arrow === 'no-gap',
                    'slide-left': slide.direction === 'left',
                    'slide-right': slide.direction === 'right',
                }"
            >
                <h5 v-if="menuTitle">{{ menuTitle }}</h5>
                <slot :close="() => { open = false; }" />
            </div>
        </Transition>
    </div>
</template>
<script setup>
import { ref, computed, reactive, nextTick, watch } from 'vue';
import vClickOutside from '~dsc/directives/click-outside';

const props = defineProps({
    text: {
        type: String,
        default: '',
    },
    buttonClass: {
        type: String,
        default: '',
    },
    arrow: {
        type: [Boolean, String],
        default: false,
        validator: v => typeof v === 'boolean' || v === 'no-gap',
    },
    menuTitle: {
        type: String,
        default: '',
    },
    elementForBounds: {
        type: null,
        default: null,
        validator: v => (v === null || typeof v?.getBoundingClientRect === 'function'),
    },
    disableFlipAndSlide: {
        type: [Boolean, Object],
        default: false,
        validator: v => (typeof v === 'boolean' || typeof v.noFlip === 'boolean' || typeof v.noSlide === 'boolean'),
    },
    slideMethod: {
        type: String,
        default: 'parent-edges',
        validator: v => ['parent-center', 'parent-edges'].includes(v),
    },
    align: {
        type: String,
        default: 'center',
    },
    listTransition: {
        type: String,
        default: 'fade',
    },
    discardMenu: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['open']);

const open = ref(false);
const menuRef = ref(null);
const buttonContainerRef = ref(null);
const flip = ref(false);
const slide = reactive({
    direction: null,
    amount: '0px',
});

const tweaks = computed(() => typeof props.disableFlipAndSlide === 'boolean'
    ? { slide: !props.disableFlipAndSlide, flip: !props.disableFlipAndSlide }
    : { slide: !props.disableFlipAndSlide.noSlide, flip: !props.disableFlipAndSlide.noFlip });

watch(open, async newValue => {
    emit('open', newValue);

    if (!open.value) return;

    flip.value = false;
    slide.direction = null;

    if (!tweaks.value.flip && !tweaks.value.slide) return;

    await nextTick();

    if (menuRef.value) {
        const bounds = menuRef.value.getBoundingClientRect();
        const parentBounds = props.elementForBounds?.getBoundingClientRect() ?? { bottom: window.innerHeight, right: window.innerWidth, left: 0, width: window.innerWidth };

        if (tweaks.value.flip) flip.value = bounds.bottom > parentBounds.bottom;
        if (!tweaks.value.slide) return;

        const setSlide = left => {
            const { width: buttonWidth = null } = buttonContainerRef.value?.getBoundingClientRect() ?? {};

            slide.direction = left ? 'left' : 'right';
            slide.amount = buttonWidth !== null ? `${buttonWidth / 2}px` : '50%';
        };

        if (props.slideMethod === 'parent-edges') {
            const slideLeft = bounds.left < parentBounds.left;
            const slideRight = bounds.right > parentBounds.right;

            if (slideLeft || slideRight) setSlide((parentBounds.left - bounds.left) > (bounds.right - parentBounds.right));
        }

        if (props.slideMethod === 'parent-center') {
            setSlide(bounds.x < (parentBounds.left + parentBounds.width / 2));
        }
    }
});

defineExpose({
    toggle: v => {
        if (v === undefined) return open.value = !open.value;

        open.value = v;
        emit('open', v);
    },
});
</script>
<style scoped>
.ds-dropdown {
  position: relative;
  display: grid;
  align-items: center;
  width: fit-content;
}

.align-start {
  align-items: flex-start;
}

.align-end {
  align-items: flex-end;
}

.button-container {
  width: 100%;
}

.menu {
  position: absolute;
  top: 100%;
  display: grid;
  width: max-content;
  min-width: 10rem;
  max-width: 20rem;
  margin-top: 0.5rem;
  border: 1px solid var(--color-base-grey-200);
  border-radius: 3px;
  background-color: var(--color-base-white-default);
  box-shadow: 0 4px 8px 0 var(--color-base-black-mine-shaft-opaque);
  z-index: 1;
}
.menu > h5 {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-base-grey-300);
  font-size: 1rem;
  font-weight: 600;
}
.menu > :slotted(button),
.menu > :slotted(a) {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-base-grey-600);
  background-color: var(--color-base-white-default);
  transition: 0.15s ease-in color, 0.15s ease-in background-color;
}
.menu > :slotted(button):not(:last-child),
.menu > :slotted(a):not(:last-child) {
  border-bottom: 1px solid var(--color-base-grey-100);
}
.menu > :slotted(button):hover,
.menu > :slotted(a):hover {
  color: inherit;
  background-color: var(--color-base-grey-100);
}
.menu > :slotted(button):active,
.menu > :slotted(a):active {
  background-color: var(--color-base-grey-200);
}

.arrow {
  margin-top: 0;
}
.arrow:not(.no-gap) {
  top: calc(100% + var(--dropdown-arrow-offset));
}
.arrow::before {
  content: "";
  border-top: 1px solid var(--color-base-grey-200);
  border-left: 1px solid var(--color-base-grey-200);
  background-color: var(--color-base-white-default);
  width: var(--dropdown-arrow-size);
  height: var(--dropdown-arrow-size);
  transform: rotate(45deg);
  position: absolute;
  top: calc(-1 * (var(--dropdown-arrow-offset) - 0.125rem));
  left: calc(50% - var(--dropdown-arrow-size) / 2);
  z-index: -1;
}
.arrow.flip::before {
  top: auto;
  bottom: calc(-1 * (var(--dropdown-arrow-offset) - 0.125rem));
}

.flip {
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 0.5rem;
}
.flip.arrow {
  margin-bottom: 0;
}
.flip.arrow:not(.no-gap) {
  top: auto;
  bottom: calc(100% + var(--dropdown-arrow-offset));
}

.slide-left {
  left: 0;
}
.slide-left.arrow::before {
  left: calc(v-bind("slide.amount") - var(--dropdown-arrow-size) / 2);
}

.slide-right {
  right: 0;
}
.slide-right.arrow::before {
  left: auto;
  right: calc(v-bind("slide.amount") - var(--dropdown-arrow-size) / 2);
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.15s;
}
</style>
